import Assessors from './pages/Assessors';
import AssessmentAiPlayground from './pages/AssessmentAiPlayground';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginForm from './components/LoginForm';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Users from './pages/Users';
import MBCBReports from './pages/MBCBReports';
import MbcbReportImport from './pages/MbcbReportImport';
import ChurchPlanters from './pages/ChurchPlanters';
import Hubs from './pages/Hubs';
import PortalCandidates from './pages/PortalCandidates';
import EmailConfig from './pages/EmailConfig';
import MbcbNotificationSettings from './pages/MbcbNotificationSettings';
import AiTools from './pages/AiTools';
import BackgroundChecks from './pages/BackgroundChecks';
import PushNotifications from './pages/PushNotifications';
import PushNotificationSetup from './pages/PushNotificationSetup';
import Trainers from './pages/Trainers';
import Coaches from './pages/Coaches';
import ChurchDirectory from './pages/ChurchDirectory';
import Assessments from './pages/Assessments';
import AssessmentDetail from './pages/AssessmentDetail';
import ScheduleTemplates from './pages/ScheduleTemplates';
import AssessmentRoles from './pages/AssessmentRoles';
import AssessmentInterviewGuides from './pages/AssessmentInterviewGuides';
import AssessmentInterviewGuideDetail from './pages/AssessmentInterviewGuideDetail';
import HubContactOverview from './pages/HubContactOverview';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route 
                path="/login" 
                element={
                  <ProtectedRoute requiresAuth={false}>
                    <LoginForm />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/forgot-password" 
                element={
                  <ProtectedRoute requiresAuth={false}>
                    <ForgotPassword />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/reset-password" 
                element={
                  <ProtectedRoute requiresAuth={false}>
                    <ResetPassword />
                  </ProtectedRoute>
                } 
              />
              
              {/* Admin feature routes */}
              <Route 
                path="/users" 
                element={
                  <ProtectedRoute>
                    <Users />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/reports" 
                element={
                  <ProtectedRoute>
                    <MBCBReports />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/reports/import"
                element={
                  <ProtectedRoute>
                    <MbcbReportImport />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/planters"
                element={
                  <ProtectedRoute>
                    <ChurchPlanters />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hubs"
                element={
                  <ProtectedRoute>
                    <Hubs />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/portal-candidates"
                element={
                  <ProtectedRoute>
                    <PortalCandidates />
                  </ProtectedRoute>
                } 
              />

              <Route path="/assessments/assessors" element={<ProtectedRoute><Assessors /></ProtectedRoute>} />
                  <Route
                path="/assessments"
                element={
                  <ProtectedRoute>
                    <Assessments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assessments/:id"
                element={
                  <ProtectedRoute>
                    <AssessmentDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/schedule-templates"
                element={
                  <ProtectedRoute>
                    <ScheduleTemplates />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assessment-roles"
                element={
                  <ProtectedRoute>
                    <AssessmentRoles />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/interview-guides"
                element={
                  <ProtectedRoute>
                    <AssessmentInterviewGuides />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/interview-guides/:guideId"
                element={
                  <ProtectedRoute>
                    <AssessmentInterviewGuideDetail />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/email-config"
                element={
                  <ProtectedRoute>
                    <EmailConfig />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/email-config/mbcb-report-submitted"
                element={
                  <ProtectedRoute>
                    <MbcbNotificationSettings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assessment-ai-playground"
                element={<ProtectedRoute><AssessmentAiPlayground /></ProtectedRoute>}
              />

              <Route
                path="/ai-tools"
                element={
                  <ProtectedRoute>
                    <AiTools />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/background-checks"
                element={
                  <ProtectedRoute>
                    <BackgroundChecks />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/push-notifications"
                element={
                  <ProtectedRoute>
                    <PushNotifications />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/push-notification-setup"
                element={
                  <ProtectedRoute>
                    <PushNotificationSetup />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/trainers"
                element={
                  <ProtectedRoute>
                    <Trainers />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/coaches"
                element={
                  <ProtectedRoute>
                    <Coaches />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/hub-contacts"
                element={
                  <ProtectedRoute>
                    <HubContactOverview />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/church-directory"
                element={
                  <ProtectedRoute>
                    <ChurchDirectory />
                  </ProtectedRoute>
                }
              />
              
              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/portal-candidates" replace />} />
              
              {/* Catch all route */}
              <Route 
                path="*" 
                element={
                  <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">
                        Page Not Found
                      </h2>
                      <p className="text-gray-600 mb-4">
                        The page you're looking for doesn't exist.
                      </p>
                      <button
                        onClick={() => window.location.href = '/'}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Go Home
                      </button>
                    </div>
                  </div>
                } 
              />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
